import { useCallback, useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl, FormattedMessage } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  ConfirmButtonCell,
  DateCell,
  DownloadButtonCell,
  MobileSortBy,
  Table,
} from 'components/Table'
import { currentUserSelector } from 'features/auth/login/selectors'
import { DomainConfig } from 'features/domains/types'

import { ConfigNameCell } from '../../components/ConfigNameCell'

import { homeConfigurationMessages } from '../../messages'

import { selectedHomeSelector } from 'features/home/selectors'
import { MobileHomeConfigFiles } from './MobileHomeConfigFiles'
import { DeleteHomeConfigCell } from './DeleteButtonCell'
import {
  HomeConfigActionCellProgressBarContainer,
  HomeConfigActionCellProgressBar,
  HomeConfigActionsCellContainer,
  HomeConfigActionsCellBody,
  HomeConfigRowStyle,
} from '../styles'
import { getSortOptions } from 'components/Table/utils'
import { TableWithPaginationWrapper } from 'components/Table/styles'
import { hasSystemAccessEnabled } from 'features/home/utils'
import { deleteDomainConfigThunk, deployDomainConfigThunk } from '../../slice'
import { deployDomainConfigSelectors } from '../../selectors'
import { extractDeployErrorMessage } from '../../utils'
import { selectSoftwareUpdatesInProgress } from 'features/home/components/HomeSoftware/HomeCloudSoftwareHistory/selectors'

enum HomeConfigHeaders {
  NAME = 'name',
  UPDATED = 'updated',
}

interface HomeConfigFilesTableProps {
  isDeployShown: boolean
  data: DomainConfig[]
}

const DEFAULT_SORT_BY = { id: 'updated', desc: true }

interface ActionsCellProps extends Cell<DomainConfig> {
  isDeployShown: boolean
}

function ActionsCell(cell: ActionsCellProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const user = useAppSelector(currentUserSelector)
  const home = useAppSelector(selectedHomeSelector.data)
  const deployError = useAppSelector(deployDomainConfigSelectors.error)
  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )

  const isUpdateInProgress = softwareUpdatesInProgress?.length > 0
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const hasSystemAccess = hasSystemAccessEnabled(home)

  const config = cell.row.original
  const { domainId, name, url, id } = config
  const fileName = `${name}.tar.gz`

  const handleDelete = useCallback(async () => {
    await dispatch(deleteDomainConfigThunk(id, domainId))
  }, [dispatch, id, domainId])

  const handleDeploy = useCallback(async () => {
    if (home?.id && user?.id) {
      setIsLoading(true)
      try {
        await dispatch(
          deployDomainConfigThunk(
            {
              config_id: id,
              home_id: home.id,
              integrator_id: user.id,
            },
            setProgress
          )
        )
      } catch (e) {
        throw e
      } finally {
        setIsLoading(false)
        setProgress(0)
      }
    }
  }, [dispatch, home?.id, id, user.id])

  return (
    <HomeConfigActionsCellContainer>
      {isLoading && (
        <HomeConfigActionCellProgressBarContainer>
          <HomeConfigActionCellProgressBar progress={progress} />
        </HomeConfigActionCellProgressBarContainer>
      )}
      <HomeConfigActionsCellBody isLoading={isLoading}>
        {hasSystemAccess && cell.isDeployShown && (
          <ConfirmButtonCell
            title={formatMessage(homeConfigurationMessages.deployTitle)}
            body={formatMessage(homeConfigurationMessages.deployBody)}
            handleConfirm={handleDeploy}
            isEnabledErrorModal
            errorMessage={extractDeployErrorMessage(deployError)}
            disabled={isUpdateInProgress}
          >
            <FormattedMessage {...homeConfigurationMessages.deployButton} />
          </ConfirmButtonCell>
        )}
        <DownloadButtonCell url={url} fileName={fileName} />
        <DeleteHomeConfigCell
          title={formatMessage(homeConfigurationMessages.deleteFileTitle)}
          handleConfirm={handleDelete}
        >
          <FormattedMessage
            {...homeConfigurationMessages.deleteConfirmText}
            values={{
              configName: <b>{name}</b>,
              br: <br />,
            }}
          />
        </DeleteHomeConfigCell>
      </HomeConfigActionsCellBody>
    </HomeConfigActionsCellContainer>
  )
}

function HomeConfigFilesTable(props: HomeConfigFilesTableProps) {
  const { data, isDeployShown } = props
  const { formatMessage } = useIntl()

  const expandedData = useMemo(
    () =>
      data.map((config) => ({
        ...config,
        isDeployShown,
      })),
    [isDeployShown, data]
  )

  const [sortBy, setSortBy] =
    useState<SortingRule<DomainConfig>>(DEFAULT_SORT_BY)

  const columns: readonly Column<DomainConfig>[] = useMemo(
    () => [
      {
        Header: formatMessage(homeConfigurationMessages.headerName),
        accessor: 'name',
        Cell: ConfigNameCell,
        percentWidth: '42%',
      },
      {
        Header: formatMessage(homeConfigurationMessages.headerUploaded),
        accessor: 'updated',
        Cell: DateCell,
        percentWidth: '42%',
      },
      {
        Header: '',
        accessor: 'id',
        disableSortBy: true,
        id: 'actions',
        Cell: (props: Cell<DomainConfig>) => (
          <ActionsCell {...props} isDeployShown={isDeployShown} />
        ),
        percentWidth: '16%',
      },
    ],
    [isDeployShown, formatMessage]
  )

  const headers = useMemo(
    () => ({
      name: {
        label: formatMessage(homeConfigurationMessages.headerName),
        id: HomeConfigHeaders.NAME,
      },
      updated: {
        label: formatMessage(homeConfigurationMessages.headerUploaded),
        id: HomeConfigHeaders.UPDATED,
      },
    }),
    [formatMessage]
  )

  const sortOptions = useMemo(() => getSortOptions(headers), [headers])

  return (
    <TableWithPaginationWrapper>
      <MobileSortBy
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <Table
        columns={columns}
        data={expandedData}
        defaultSortBy={DEFAULT_SORT_BY}
        rowStyles={HomeConfigRowStyle}
        sortBy={sortBy}
        setSortBy={setSortBy}
        mobileViewComponent={MobileHomeConfigFiles}
      />
    </TableWithPaginationWrapper>
  )
}

export default HomeConfigFilesTable
